import { Card, CardActionArea, Chip, Stack, Typography } from '@mui/material'
import { alpha } from '@mui/material/styles'
import type { OrderRowStatus } from '@commerce/core'
import { YALO_SEMANTIC, YALO_SHADOW } from '@commerce/tokens'

/**
 * The tint and the text colour the design gives each status.
 *
 * The tints are the `YALO_SEMANTIC.status*` tokens at the alphas the design
 * authored (`reference.html` line 1442), which is why they are built with
 * `alpha()` rather than pasted as `rgba()` literals — the token stays the single
 * source of the hue. The foregrounds are *not* derived from the tokens: mint at
 * 20% is unreadable as text, so the design carries a separate darkened shade for
 * each, and those are copied as authored.
 *
 * The design drew three states; the API returns thirteen, so three more labels
 * exist to keep the mapping honest (see `orderStatusLabel`). They reuse the
 * authored tints rather than inventing hues: "Enviado" is on its way and shares
 * the info blue, "En proceso" is deliberately neutral, and "Con error" borrows
 * the danger red because it needs attention — while still saying which it is,
 * since the word, not the colour, is what carries the meaning.
 */
const STATUS_STYLE: Record<OrderRowStatus, { bg: string; fg: string }> = {
  Entregado: { bg: alpha(YALO_SEMANTIC.statusSuccess, 0.2), fg: '#0B6B4C' },
  Enviado: { bg: alpha(YALO_SEMANTIC.statusInfo, 0.14), fg: '#046A80' },
  Confirmado: { bg: alpha(YALO_SEMANTIC.statusInfo, 0.14), fg: '#046A80' },
  'En proceso': { bg: alpha(YALO_SEMANTIC.fg2, 0.1), fg: YALO_SEMANTIC.fg2 },
  Cancelado: { bg: alpha(YALO_SEMANTIC.statusDanger, 0.12), fg: '#B03A52' },
  'Con error': { bg: alpha(YALO_SEMANTIC.statusDanger, 0.12), fg: '#B03A52' },
}

/**
 * The status, as a badge that still says the status.
 *
 * The design distinguishes the three states by colour alone. Colour alone fails
 * a greyscale screenshot, a colour-blind reader and a screen reader, so the
 * label is the status word itself and the tint is decoration on top of it.
 */
export const OrderStatusBadge = ({ status }: { status: OrderRowStatus }) => (
  <Chip
    size="small"
    label={status}
    sx={{
      height: 22,
      fontSize: 11.5,
      bgcolor: STATUS_STYLE[status].bg,
      color: STATUS_STYLE[status].fg,
    }}
  />
)

/**
 * "9 artículos", "1 artículo".
 *
 * Shared by the list and the detail screen so the two never disagree about the
 * singular. It is a label, not a calculation: the count is given.
 */
export const itemCountLabel = (count: number): string =>
  `${count} ${count === 1 ? 'artículo' : 'artículos'}`

export interface OrderCardModel {
  /** Routing identity — what `onOpen` hands back. */
  id: string
  /**
   * What the row shows as the order's name. The API's `id` is a Mongo
   * ObjectId, which is correct and unreadable, so a sequence or external
   * reference is preferred when the order carries one.
   */
  reference?: string
  date: string
  status: OrderRowStatus
  itemCountText: string
  /** Already formatted by `toPriceText`; empty when the storefront hides prices. */
  totalText: string
}

interface Props {
  model: OrderCardModel
  onOpen: (orderId: string) => void
}

/**
 * One row of the order list.
 *
 * The whole row is the affordance in the design, so the whole row is a real
 * button — `CardActionArea`, not an `onClick` on a `<div>`. Its accessible name
 * carries every field the row draws, because four separate texts read out one
 * at a time do not tell a screen-reader user which order they are on.
 */
export const OrderCard = ({ model, onOpen }: Props) => {
  const shown = model.reference || model.id
  const name = ['Pedido ' + shown, model.date, model.itemCountText, model.totalText]
    .filter(Boolean)
    .join(', ')

  return (
    <Card variant="outlined" sx={{ boxShadow: YALO_SHADOW.xs }}>
      <CardActionArea
        onClick={() => onOpen(model.id)}
        aria-label={`${name}. Estado: ${model.status}`}
        sx={{ p: 1.75 }}
      >
        <Stack spacing={1}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
            <Typography variant="body2" fontWeight={700}>
              {shown}
            </Typography>
            <OrderStatusBadge status={model.status} />
          </Stack>
          <Stack direction="row" justifyContent="space-between" alignItems="baseline" spacing={1}>
            <Typography variant="caption" color="text.secondary">
              {model.date} · {model.itemCountText}
            </Typography>
            {/* `toPriceText` returns an empty string for a storefront that hides
                prices, and an empty bold line reads as a rendering bug. */}
            {model.totalText && (
              <Typography variant="body1" fontWeight={700}>
                {model.totalText}
              </Typography>
            )}
          </Stack>
        </Stack>
      </CardActionArea>
    </Card>
  )
}
